/**
 * Composant pour afficher le clavier PIN
 * @returns 
 */
import { Button } from "../ui/button";
import { X, Check, Delete, RefreshCw } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";

interface PinKeypadProps {
  title: string;
  onSubmit: (pin: string) => void;
  onCancel: () => void;
  error?: string;
}

const digits = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "delete"];

export const PinKeypad = ({
  title,
  onSubmit,
  onCancel,
  error,
}: PinKeypadProps) => {
  const [pin, setPin] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Réinitialiser le PIN en cas d'erreur
  useEffect(() => {
    if (error) {
      setPin("");
    }
  }, [error]);

  const handleDigit = (digit: string) => {
    if (pin.length < 6) {
      setPin(pin + digit);
    }
  };

  const handleDelete = () => {
    setPin(pin.slice(0, -1));
  };

  const handleSubmit = async () => {
    if (pin.length >= 4 && pin.length <= 6) {
      setIsLoading(true);
      try {
        await onSubmit(pin);
        setPin("");
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-foreground/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="gradient-primary p-6 text-center">
          <h2 className="text-xl font-semibold text-primary-foreground">
            {title}
          </h2>
          <p className="text-primary-foreground/80 text-sm mt-1">
            Entrez votre code employé
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* PIN Display */}
          <div className="flex justify-center gap-3">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className={cn(
                  "w-4 h-4 rounded-full border-2 transition-all duration-200",
                  pin.length > i
                    ? "bg-primary border-primary scale-110"
                    : "border-muted-foreground/30"
                )}
              />
            ))}
          </div>

          {/* Error message */}
          {error && (
            <div className="text-destructive text-sm text-center font-medium bg-destructive/10 py-2 px-4 rounded-lg">
              {error}
            </div>
          )}

          {/* Keypad */}
          <div className="grid grid-cols-3 gap-3">
            {digits.map((digit, index) => {
              if (digit === "") {
                return <div key={index} />;
              }
              if (digit === "delete") {
                return (
                  <button
                    key={index}
                    onClick={handleDelete}
                    disabled={pin.length === 0}
                    className="h-16 rounded-xl flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors disabled:opacity-30"
                  >
                    <Delete className="w-6 h-6" />
                  </button>
                );
              }
              return (
                <button
                  key={index}
                  onClick={() => handleDigit(digit)}
                  disabled={pin.length >= 6}
                  className="h-16 rounded-xl text-2xl font-semibold text-foreground hover:bg-muted transition-colors active:scale-95 disabled:opacity-30"
                >
                  {digit}
                </button>
              );
            })}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1 h-12"
              onClick={onCancel}
              disabled={isLoading}
            >
              <X className="w-4 h-4 mr-2" />
              Annuler
            </Button>
            <Button
              className="flex-1 h-12 gradient-primary border-0"
              onClick={handleSubmit}
              disabled={pin.length < 6 || isLoading}
            >
              {isLoading ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Valider
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
